// SplashScreen.js

import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  faUserShield,
  faFile,
  faFolderOpen,
  faClone,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './SplashScreen.css';
import cursorDefault from '../../assets/cursors/cursor-default.png';
import cursorPointer from '../../assets/cursors/cursor-pointer.png';
import cursorText from '../../assets/cursors/cursor-text.png';
import { UserContext } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import LottieLoader from '../LoadingSpinner/LottieLoader';

// Images
import HeaderImage from '../../assets/Images/Header.png';
import SupportImage from '../../assets/Images/support.webp';
import TutorialsImage from '../../assets/Images/tutorials.webp';
import TutorialsRollImage from '../../assets/Images/tutorialsroll.webp';
import SupportRollImage from '../../assets/Images/supportroll.webp';
import CommunityImage from '../../assets/Images/community.webp';
import CommunityRollImage from '../../assets/Images/communityroll.webp';
import RequestImage from '../../assets/Images/request.webp';
import RequestRollImage from '../../assets/Images/requestroll.webp';
import Sample1 from '../../assets/Images/1sample.png';
import Sample2 from '../../assets/Images/2sample.png';
import Sample3 from '../../assets/Images/3sample.png';
// Social Icons
import FacebookIcon from '../../assets/Images/icons/facebook.svg';
import DiscordIcon from '../../assets/Images/icons/discord.svg';
import YoutubeIcon from '../../assets/Images/icons/youtube.svg';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';

// Sounds

// Component
import TemplatesModal from '../TemplatesModal/TemplatesModal';
import Walkthrough from '../Walkthrough/Walkthrough';
const SplashScreen = ({ onNew, onLoad, onTemplates }) => {
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [currentTutorialsImage, setCurrentTutorialsImage] = useState(TutorialsImage);
  const [currentSupportImage, setCurrentSupportImage] = useState(SupportImage);
  const [currentCommunityImage, setCurrentCommunityImage] = useState(CommunityImage);
  const [currentRequestImage, setCurrentRequestImage] = useState(RequestImage);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All Worksheets");
  const [searchTerm, setSearchTerm] = useState('');
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesLoadError, setTemplatesLoadError] = useState('');
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const templatesRequestIdRef = useRef(0);
  const BACKEND_BASE_URL = 'https://edugroove.com/edugroove_backend/templates';
  const [categories, setCategories] = useState(["All Worksheets"]);
  const [isPreparingSplash, setIsPreparingSplash] = useState(true);
  const [isSplashTourOpen, setIsSplashTourOpen] = useState(false);
  const [splashTourStep, setSplashTourStep] = useState(0);
  const [hasCheckedAutomaticTour, setHasCheckedAutomaticTour] =
    useState(false);
  const splashCursorVars = {
    '--splash-cursor-default': `url(${cursorDefault}) 2 2, default`,
    '--splash-cursor-pointer': `url(${cursorPointer}) 6 2, pointer`,
    '--splash-cursor-text': `url(${cursorText}) 0 0, text`,
  };

  useEffect(() => {
    let isMounted = true;

    const prepareSplashScreen = async () => {
      setIsPreparingSplash(true);

      try {
        const imageList = [
          HeaderImage,
          SupportImage,
          TutorialsImage,
          TutorialsRollImage,
          SupportRollImage,
          CommunityImage,
          CommunityRollImage,
          Sample1,
          Sample2,
          Sample3,
          RequestImage,
          RequestRollImage,
          FacebookIcon,
          YoutubeIcon
        ];

        const imagePromises = imageList.map((src) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve;
            img.src = src;
          });
        });

        const categoriesPromise = fetch(
          `https://edugroove.com/edugroove_backend/get_categories.php?v=${Date.now()}`,
          {
            cache: 'no-store',
          }
        )
          .then(res => res.json())
          .then(folders => {
            if (isMounted && Array.isArray(folders)) {
              setCategories(["All Worksheets", ...folders]);
            }
          })
          .catch(err => {
            console.error('Failed to fetch categories:', err);
          });

        await Promise.all([
          ...imagePromises,
          categoriesPromise
        ]);
      } finally {
        if (isMounted) {
          setIsPreparingSplash(false);
        }
      }
    };

    prepareSplashScreen();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch once when the modal opens. fetchTemplates can recover the category
  // list itself if the initial splash-screen request failed.
  useEffect(() => {
    if (showTemplatesModal) {
      fetchTemplates("All Worksheets");
    }
  }, [showTemplatesModal]);


  const { currentUser, saveCurrentUser } = useContext(UserContext);

  const isBasic = currentUser?.planRole === 'armember_basic';
  const isSolo = currentUser?.planRole === 'armember_solo';
  const isPartnerSchool =
    currentUser?.planRole === 'armember_partnerschool';

  const canAccessSchoolBranding =
    Boolean(currentUser) &&
    !isPartnerSchool &&
    Boolean(currentUser.isAdmin || currentUser.subUserLimit > 0);

  const tourStorageUserId = currentUser?.id || 'guest';

  const tourCompletedStorageKey =
    `edugroove_walkthrough_completed_${tourStorageUserId}`;

  const splashTourSteps = [
    {
      selector: null,
      placement: 'center',
      eyebrow: 'Welcome',
      title: 'Welcome to EDU Groove',
      description: (
        <>
          <p>
            This short tour will show you how to begin a worksheet
            and where to find the main creation tools.
          </p>
          <p>
            It starts here and continues inside the worksheet editor.
          </p>
        </>
      ),
    },
    {
      selector: '[data-tour="splash-new"]',
      placement: 'right',
      eyebrow: 'Start',
      title: 'Create a blank worksheet',
      description: (
        <p>
          Choose New when you want to begin with a clean blank page.
        </p>
      ),
    },
    {
      selector: '[data-tour="splash-load"]',
      placement: 'right',
      eyebrow: 'Continue',
      title: 'Open previous work',
      description: (
        <p>
          Load opens a saved EDU Groove .groove project so you can
          continue editing it.
        </p>
      ),
    },
    {
      selector: '[data-tour="splash-templates"]',
      placement: 'right',
      eyebrow: 'Quick Start',
      title: 'Begin with a template',
      description: (
        <p>
          Templates give you ready-made activities that can still be
          completely changed and personalised.
        </p>
      ),
    },
    ...(canAccessSchoolBranding
  ? [
      {
        selector: '[data-tour="splash-school-branding"]',
        placement: 'right',
        eyebrow: 'School Branding',
        title: 'Add your school logo',
        description: (
          <>
            <p>
              Open School Branding to upload or change your school
              logos.
            </p>

            <p>
              Your selected logo can then appear automatically in
              compatible EDU Groove templates, saving you from adding
              it manually each time.
            </p>
          </>
        ),
      },
    ]
  : []),
    {
      selector: '[data-tour="splash-learning-links"]',
      placement: 'bottom',
      eyebrow: 'Help',
      title: 'Find tutorials and support',
      description: (
        <>
          <p>
            These links take you to tutorials, support and the resource
            request page.
          </p>
          <p>
            Next, we will enter a blank worksheet and look around the
            editor.
          </p>
        </>
      ),
      nextLabel: 'Enter the Workspace',
    },
  ];
  useEffect(() => {
    if (isPreparingSplash) return;
    if (hasCheckedAutomaticTour) return;

    setHasCheckedAutomaticTour(true);

    const hasCompletedTour =
      localStorage.getItem(tourCompletedStorageKey) === 'true';

    if (!hasCompletedTour) {
      window.setTimeout(() => {
        setSplashTourStep(0);
        setIsSplashTourOpen(true);
      }, 350);
    }
  }, [
    isPreparingSplash,
    hasCheckedAutomaticTour,
    tourCompletedStorageKey,
  ]);

  const startSplashTour = () => {
    setSplashTourStep(0);
    setIsSplashTourOpen(true);
  };

  const closeSplashTour = () => {
    localStorage.setItem(
      tourCompletedStorageKey,
      'true'
    );

    sessionStorage.removeItem(
      'edugroove_continue_walkthrough'
    );

    setIsSplashTourOpen(false);
    setSplashTourStep(0);
  };

  const finishSplashTourAndEnterWorkspace = () => {
    sessionStorage.setItem(
      'edugroove_continue_walkthrough',
      'true'
    );

    setIsSplashTourOpen(false);
    setSplashTourStep(0);

    onNew();
  };

  
  const navigate = useNavigate();

  useEffect(() => {    

    [TutorialsRollImage, SupportRollImage, CommunityRollImage, RequestRollImage].forEach(image => {
      const img = new Image();
      img.src = image;
    });

  }, []);

  

  const handleLoadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.groove,.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        setIsLoadingFile(true);
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const jsonContent = JSON.parse(reader.result);

            if (jsonContent.fileType !== 'EDUGROOVE') {
              alert('This file is not a valid EDU Groove worksheet.');
              setIsLoadingFile(false);
              return;
            }

            onLoad(jsonContent);
          } catch (error) {
            alert('Failed to load file. Please ensure it is a valid EDU Groove file.');
          } finally {
            setIsLoadingFile(false);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleTemplateClick = () => {
    setIsLoadingTemplates(true); // ✅ immediate
    setTemplatesLoadError('');
    setShowTemplatesModal(true);
    setSelectedCategory("All Worksheets");
    setSearchTerm('');
  };

  const handleCloseTemplatesModal = () => {
    // Invalidate any request that is still running after the modal closes.
    templatesRequestIdRef.current += 1;
    setShowTemplatesModal(false);
    setSelectedCategory(null);
    setTemplates([]);
    setIsLoadingTemplates(false);
    setTemplatesLoadError('');
    setSearchTerm('');
  };

  const wait = (milliseconds) =>
    new Promise(resolve => window.setTimeout(resolve, milliseconds));

  const fetchJsonWithRetry = async (url, attempts = 3) => {
    let lastError;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        const response = await fetch(url, { cache: 'no-cache' });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        lastError = error;

        if (attempt < attempts) {
          // A short increasing delay helps temporary mobile/slow connections.
          await wait(500 * attempt);
        }
      }
    }

    throw lastError || new Error('Request failed');
  };

  const fetchTemplates = async (category) => {
    const requestId = templatesRequestIdRef.current + 1;
    templatesRequestIdRef.current = requestId;

    setIsLoadingTemplates(true);
    setTemplatesLoadError('');

    try {
      let allTemplates = [];
      const BACKEND_BASE_URL = 'https://edugroove.com/edugroove_backend/templates';

      if (category === "All Worksheets") {
        // Prefer categories from state; if empty, fetch them now (slow connections)
        let cats = categories.filter(c => c && c !== "All Worksheets");
        if (cats.length === 0) {
          const folders = await fetchJsonWithRetry(
            'https://edugroove.com/edugroove_backend/get_categories.php'
          );

          if (!Array.isArray(folders) || folders.length === 0) {
            throw new Error('No template categories were returned');
          }

          cats = folders;

          if (requestId === templatesRequestIdRef.current) {
            setCategories(["All Worksheets", ...folders]);
          }
        }

        const results = await Promise.all(
          cats.map(async (cat) => {
            const indexUrl =
              `${BACKEND_BASE_URL}/${cat}/index.json`;
            try {
              const templatesList = await fetchJsonWithRetry(indexUrl);

              if (!Array.isArray(templatesList)) {
                throw new Error('Invalid template index');
              }

              return {
                category: cat,
                templates: templatesList.map((template) => ({
                  ...template,
                  category: cat,
                  firstPage: null
                }))
              };
            } catch (err) {
              console.warn(`❌ Failed to load index for category ${cat}`, err);
              return { category: cat, templates: null };
            }
          })
        );

        if (requestId !== templatesRequestIdRef.current) return;

        const failedCategories = results
          .filter(result => result.templates === null)
          .map(result => result.category);

        allTemplates = results
          .filter(result => Array.isArray(result.templates))
          .flatMap(result => result.templates);

        if (failedCategories.length === cats.length) {
          throw new Error('The template library could not be downloaded');
        }

        allTemplates.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTemplates(allTemplates);

        if (failedCategories.length > 0) {
          setTemplatesLoadError(
            `Some template categories could not be loaded. Please try again.`
          );
        }
      } else {
        const indexUrl =
          `${BACKEND_BASE_URL}/${category}/index.json`;
        const templatesList = await fetchJsonWithRetry(indexUrl);

        if (!Array.isArray(templatesList)) {
          throw new Error('Invalid template index');
        }

        if (requestId !== templatesRequestIdRef.current) return;

        const templatesWithCategory = templatesList.map((template) => ({
          ...template,
          category,
          firstPage: null
        }));
        templatesWithCategory.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTemplates(templatesWithCategory);
      }

      setSelectedCategory(category);
    } catch (error) {
      if (requestId === templatesRequestIdRef.current) {
        // Do not erase a previously successful list if a refresh fails.
        setTemplatesLoadError(
          'We could not load the template library. Check your connection and try again.'
        );
        console.error('Failed to load templates:', error);
      }
    } finally {
      // An older request must not stop the loader for a newer request.
      if (requestId === templatesRequestIdRef.current) {
        setIsLoadingTemplates(false);
      }
    }
  };


    const handleTemplateLoad = async (templateFile, category, template) => {
      setIsLoadingTemplate(true);

      try {
        const BACKEND_BASE_URL =
          'https://edugroove.com/edugroove_backend/templates';

        const response = await fetch(
          `${BACKEND_BASE_URL}/${category}/${templateFile}?v=${Date.now()}`,
          {
            cache: 'no-store',
          }
        );

        const templateData = await response.json();

        onTemplates({
          ...templateData,
          ...template,
        });

        handleCloseTemplatesModal();
      } catch (error) {
        alert(`Failed to load the selected template.\nError: ${error.message}`);
      } finally {
        setIsLoadingTemplate(false);
      }
    };
  

  const usefulLinksData = [
    {
      id: 1,
      image: Sample1,
      alt: 'EDU Groove',
      text: 'Find ideas and info on the EDU Groove Youtube channel!',
      link: 'https://www.youtube.com/@EDUGrooveHub',
    },
    {
      id: 2,
      image: Sample2,
      alt: 'Facebook Group',
      text: 'Share ideas & ask questions in our members only group!',
      link: 'https://www.facebook.com/groups/edugroove',
    },
    {
      id: 3,
      image: Sample3,
      alt: 'Facebook Page',
      text: 'Please like and support the EDU Groove Facebook page!',
      link: 'https://www.facebook.com/edugrooveofficial/',
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('edugroove_user');
    localStorage.removeItem('edugroove_login_saved_at');
    localStorage.removeItem('currentLoginLogId');
    localStorage.removeItem('edugroove_access_token');

    saveCurrentUser(null);
    navigate('/');
  };

  return (
    <div
      className="splash-screen splash-screen-cursors"
      style={splashCursorVars}
    >
      <div className="splashsidebar">
      <div className="sidebar-buttons">
        <button
          data-tour="splash-new"
          disabled={isPreparingSplash}
          onClick={() => {
            onNew();
          }}
          className="splashsidebar-button"
          aria-label="Create New Project"
        >
          <FontAwesomeIcon icon={faFile} />
          <span>New</span>
        </button>
        {currentUser && (!isBasic || isSolo) && (
          <button
            data-tour="splash-load"
            onClick={handleLoadClick}
            className="splashsidebar-button"
            aria-label="Load Project"
          >
            <FontAwesomeIcon icon={faFolderOpen} />
            <span>Load</span>
          </button>
        )}
        <button
          data-tour="splash-templates"
          disabled={isPreparingSplash}
          onClick={handleTemplateClick}
          className="splashsidebar-button"
          aria-label="Choose Template"
        >
          <FontAwesomeIcon icon={faClone} />
          <span>Templates</span>
        </button>
        {canAccessSchoolBranding && (
          <button
            type="button"
            data-tour="splash-school-branding"
            onClick={() => {
              navigate('/user-admin');
            }}
            className="splashsidebar-button"
            aria-label="Manage School Branding"
            title="Manage School Branding"
          >
            <FontAwesomeIcon icon={faUserShield} />
            <span>Branding</span>
          </button>
        )}

        <button
          type="button"
          onClick={startSplashTour}
          className="splashsidebar-button"
          aria-label="Take the EDU Groove Tour"
          title="Take the EDU Groove Tour"
        >
          <FontAwesomeIcon icon={faCircleQuestion} />
          <span>Tour</span>
        </button>


        <div className="splash-community-section" onMouseEnter={() => { setCurrentCommunityImage(CommunityRollImage);}} onMouseLeave={() => setCurrentCommunityImage(CommunityImage)}>
          <img src={currentCommunityImage} alt="Join Our Community" className="community-image" />
          <div className="social-icons">
            <a href="https://www.facebook.com/profile.php?id=61569095273653" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <img src={FacebookIcon} alt="Facebook" className="social-icon" />
            </a>            
            <a href="https://www.youtube.com/@EDUGrooveHub" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <img src={YoutubeIcon} alt="YouTube" className="social-icon" />
            </a>
          </div>
        </div>
        <div className="splashsidebar-button logout-button logout-red" onClick={handleLogout} title="Logout">
          <FontAwesomeIcon icon={faSignOutAlt} />
        </div>
        </div>
      </div>

      <div className="main-content">
        <div className="logo-header">
          <img src={HeaderImage} alt="Logo Header" className="logo-image" />          
        </div>

        <div
            className="links-section"
            data-tour="splash-learning-links"
          >
          <div className="link-box">
            <a href="https://edugroove.com/docs" target="_blank" rel="noopener noreferrer">
              <img src={currentTutorialsImage} alt="Tutorial" className="link-image" onMouseEnter={() => { setCurrentTutorialsImage(TutorialsRollImage);}} onMouseLeave={() => setCurrentTutorialsImage(TutorialsImage)} />
            </a>
          </div>
          <div className="link-box">
            <a href="https://edugroove.com/contact/" target="_blank" rel="noopener noreferrer">
              <img src={currentSupportImage} alt="Support" className="link-image" onMouseEnter={() => { setCurrentSupportImage(SupportRollImage);}} onMouseLeave={() => setCurrentSupportImage(SupportImage)} />
            </a>
          </div>
          <div className="link-box">
            <a href="https://edugroove.com/contact-material-request" target="_blank" rel="noopener noreferrer">
              <img src={currentRequestImage} alt="Requesr Resource" className="link-image" onMouseEnter={() => { setCurrentRequestImage(RequestRollImage);}} onMouseLeave={() => setCurrentRequestImage(RequestImage)} />
            </a>
          </div>          
        </div>

        <h3>Useful Links</h3>
        <div className="useful-links">
          {usefulLinksData.map(link => (
            <div className="link-box" key={link.id}>
              <img src={link.image} alt={link.alt} className="link-image" />
              <p>{link.text}</p>
              <a href={link.link} target="_blank" rel="noopener noreferrer" className="visit-button">
                Visit
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className={`splash-preparing-overlay ${isPreparingSplash ? 'show' : ''}`}>
        {isPreparingSplash && (
          <div className="splash-preparing-box">
            <div className="splash-preparing-spinner"></div>
            <div className="splash-preparing-title">Preparing tools</div>
            <div className="splash-preparing-subtitle">Getting everything ready...</div>
          </div>
        )}
      </div>

      {isLoadingFile && <LottieLoader message="Loading file" />}


      {isLoadingTemplates && showTemplatesModal && <LottieLoader message="Loading templates" />}


      {isLoadingTemplate && <LottieLoader message="Loading template" />}

      {isSplashTourOpen && !isPreparingSplash && (
        <Walkthrough
          steps={splashTourSteps}
          stepIndex={splashTourStep}
          onStepChange={setSplashTourStep}
          onClose={closeSplashTour}
          onFinish={finishSplashTourAndEnterWorkspace}
        />
      )}


      {showTemplatesModal && (
        <TemplatesModal
          templates={templates}
          categories={categories}
          selectedCategory={selectedCategory}
          searchTerm={searchTerm}
          onClose={handleCloseTemplatesModal}
          onLoadTemplate={handleTemplateLoad}
          onSearchTermChange={setSearchTerm}
          setTemplates={setTemplates}
          onFetchTemplates={fetchTemplates}
          isLoadingTemplates={isLoadingTemplates}
          templatesLoadError={templatesLoadError}
        />
      )}
    </div>
  );
};

export default SplashScreen;
